import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle,
  Security,
  Speed,
  Cloud,
  Storage,
  Lock,
  Visibility,
  Timeline,
  AccountTree,
  Description,
  Verified,
  ArrowBack
} from '@mui/icons-material';

const ProyectoPresentacion = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [expandedPanel, setExpandedPanel] = useState('panel1');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const caracteristicas = [
    {
      icon: <AccountTree />,
      titulo: 'Sistema de Workflow',
      descripcion: 'Gestión completa del ciclo de vida de inscripciones con estados y transiciones automáticas',
      color: 'primary'
    },
    {
      icon: <Description />,
      titulo: 'Gestión Documental',
      descripcion: 'Carga, almacenamiento seguro y validación de documentos con trazabilidad completa',
      color: 'success'
    },
    {
      icon: <Verified />,
      titulo: 'Integración Blockchain',
      descripcion: 'Certificación de integridad de documentos mediante hashes criptográficos inmutables',
      color: 'secondary'
    },
    {
      icon: <Visibility />,
      titulo: 'Mapa Interactivo',
      descripcion: 'Visualización geográfica en tiempo real del estado de proyectos por municipio',
      color: 'info'
    },
    {
      icon: <Timeline />,
      titulo: 'Dashboard Analítico',
      descripcion: 'Métricas y KPIs en tiempo real con visualizaciones interactivas',
      color: 'warning'
    },
    {
      icon: <Security />,
      titulo: 'Seguridad Multinivel',
      descripcion: 'Autenticación, autorización por roles y encriptación de datos sensibles',
      color: 'error'
    }
  ];

  const modulos = [
    {
      titulo: 'Registro de Entidades',
      descripcion: 'Permite el registro y gestión de entidades territoriales con información completa (departamento, municipio, contacto, etc.)',
      funcionalidades: ['Formulario validado', 'Gestión CRUD completa', 'Búsqueda y filtros avanzados']
    },
    {
      titulo: 'Dashboard de Inscripción',
      descripcion: 'Panel de control personalizado para cada entidad con el estado actual de su proceso de inscripción',
      funcionalidades: ['Visualización de estado', 'Progreso del workflow', 'Acciones disponibles por etapa']
    },
    {
      titulo: 'Carga de Documentos',
      descripcion: 'Sistema seguro de carga y validación de documentos requeridos para el proceso',
      funcionalidades: ['Upload con validación', 'Almacenamiento encriptado', 'Generación de hash SHA-256']
    },
    {
      titulo: 'Revisión Administrativa',
      descripcion: 'Panel de administración para validar y aprobar documentos cargados por las entidades',
      funcionalidades: ['Validación de documentos', 'Aprobación/rechazo', 'Comentarios y observaciones']
    },
    {
      titulo: 'Historial y Trazabilidad',
      descripcion: 'Registro completo e inmutable de todas las acciones realizadas en el sistema',
      funcionalidades: ['Auditoría completa', 'Timestamps certificados', 'Verificación blockchain']
    }
  ];

  const tecnologias = {
    frontend: ['React 18', 'Material-UI', 'Redux Toolkit', 'React Router', 'Leaflet Maps'],
    backend: ['.NET Core 8', 'Entity Framework', 'ASP.NET Web API', 'JWT Authentication'],
    blockchain: ['Nethereum', 'Ethereum/Hyperledger', 'Smart Contracts', 'SHA-256 Hashing'],
    infraestructura: ['Azure Blob Storage', 'SQL Server', 'Azure Key Vault', 'Docker']
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 8 }}>
      <Container maxWidth="lg">
        {/* Botón de regreso */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 4 }}
        >
          Volver al inicio
        </Button>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" fontWeight="bold" gutterBottom>
            Tracker ON
          </Typography>
          <Typography variant="h5" color="text.secondary" paragraph>
            Sistema Integral de Gestión y Seguimiento de Proyectos
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            Plataforma avanzada para el seguimiento, gestión y certificación de procesos de inscripción 
            de entidades territoriales en Colombia, con tecnología blockchain para garantizar integridad y trazabilidad.
          </Typography>
        </Box>

        {/* Características Principales */}
        <Box sx={{ mb: 8 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom textAlign="center" mb={4}>
                Características Principales
            </Typography>
        
            {/* 👇 CSS Grid puro - SIMPLE Y DIRECTO */}
            <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: {
                xs: '1fr',                    // 1 columna en móvil
                md: 'repeat(2, 1fr)',         // 2 columnas en tablet
                lg: 'repeat(3, 1fr)'          // 3 columnas en desktop
                },
                gap: 3
            }}>
                {caracteristicas.map((item, index) => (
                <Card 
                    key={index}
                    sx={{ 
                    height: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 6
                    }
                    }}
                >
                    <CardContent sx={{ 
                    flexGrow: 1,
                    display: 'flex', 
                    flexDirection: 'column',
                    p: 3
                    }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        gap: 2, 
                        mb: 2
                    }}>
                        <Box 
                        sx={{ 
                            bgcolor: `${item.color}.100`, 
                            color: `${item.color}.main`,
                            p: 1.5,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            flexShrink: 0
                        }}
                        >
                        {item.icon}
                        </Box>
                        <Typography 
                        variant="h6" 
                        fontWeight="bold"
                        >
                        {item.titulo}
                        </Typography>
                    </Box>
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                    >
                        {item.descripcion}
                    </Typography>
                    </CardContent>
                </Card>
                ))}
            </Box>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 8 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Módulos del Sistema" />
            <Tab label="Stack Tecnológico" />
            <Tab label="Integración Blockchain" />
          </Tabs>

          {/* Tab Módulos */}
          {tabValue === 0 && (
            <Box sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom mb={3}>
                Módulos Funcionales
              </Typography>
              {modulos.map((modulo, index) => (
                <Accordion 
                  key={index}
                  expanded={expandedPanel === `panel${index + 1}`}
                  onChange={handleAccordionChange(`panel${index + 1}`)}
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircle color="primary" />
                      <Typography variant="h6" fontWeight="medium">
                        {modulo.titulo}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      {modulo.descripcion}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Funcionalidades:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {modulo.funcionalidades.map((func, idx) => (
                        <Chip 
                          key={idx}
                          label={func}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {/* Tab Stack Tecnológico */}
            {tabValue === 1 && (
                <Box sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom mb={3}>
                    Tecnologías Implementadas
                    </Typography>
                    
                    {/* CSS Grid puro */}
                    <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, 1fr)'
                    },
                    gap: 3
                    }}>
                    {Object.entries(tecnologias).map(([categoria, techs], index) => (
                        <Card 
                        key={index}
                        sx={{ 
                            height: 200,
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        >
                        <CardContent sx={{ 
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Typography 
                            variant="h6" 
                            fontWeight="bold" 
                            gutterBottom 
                            textTransform="capitalize"
                            >
                            {categoria}
                            </Typography>
                            <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 1, 
                            mt: 2
                            }}>
                            {techs.map((tech, idx) => (
                                <Chip 
                                key={idx}
                                label={tech}
                                color="primary"
                                variant="filled"
                                size="small"
                                />
                            ))}
                            </Box>
                        </CardContent>
                        </Card>
                    ))}
                    </Box>
                </Box>
            )}

          {/* Tab Blockchain - Lo completaré en el siguiente mensaje */}
          {tabValue === 2 && (
            <Box sx={{ p: 4 }}>
              <Button 
                variant="contained" 
                onClick={() => navigate('/blockchain-integration')}
                fullWidth
              >
                Ver Documentación Completa de Blockchain
              </Button>
            </Box>
          )}
        </Paper>

        {/* Arquitectura */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom textAlign="center" mb={4}>
            Arquitectura del Sistema
          </Typography>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Cloud sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Frontend React
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      SPA moderna con Material-UI, gestión de estado con Redux y enrutamiento dinámico
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Speed sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Backend .NET Core
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      API RESTful robusta con arquitectura en capas y patrones de diseño enterprise
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Lock sx={{ fontSize: 64, color: 'secondary.main', mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Blockchain Layer
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Certificación inmutable de documentos mediante hashes en red blockchain
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>

        {/* Fase 2 - Mejoras */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom textAlign="center" mb={4}>
            Fase 2 - Mejoras
          </Typography>
          
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)'
            },
            gap: 3
          }}>
            <Card sx={{ 
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}>
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Comités
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Clasificación de gestores en comités de la Institución
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    onClick={() => navigate('/comites-arquitectura')}
                    fullWidth
                  >
                    Arquitectura
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ 
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}>
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  NFT
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  NFT Cooperativas vivienda
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    onClick={() => navigate('/nft-cooperativas')}
                    fullWidth
                  >
                    Presentación
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

      </Container>
    </Box>
  );
};

export default ProyectoPresentacion;